import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function SubjectForm() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      code: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: organizationId,
    },
  });

  const [subjects, setsubjects] = useState([]);
  const fetchsubjects = async () => {
    try {
      const response = await get(
        `Subject/GetAllSubjectByOrganizationId?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setsubjects(response.subjects);
    } catch (error) {
      console.error("Error fetching subject:", error);
    }
  };
  useEffect(() => {
    fetchsubjects();
  }, [triggerRerender]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "code",
        header: "Code",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEditClick(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0 d-none" // Kept d-none as per original
              onClick={() => handleDeleteClick(row.original.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = subjects.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Code: row.code,
      // ID: row.id,
    }));

    console.log(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subjects");
    XLSX.writeFile(workbook, "Subjects.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  /* eslint-disable no-unused-vars */
  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const domain = (process.env.REACT_APP_LIVE_DOMAIN_URL || "").replace(/\/$/, "");
    
    const cleanPath = (path) => (path ? path.replace(/^\/+/, "") : "");
    const orgLogo = orgLogoPath ? `${domain}/ExternalImages/${cleanPath(orgLogoPath)}` : null;
    const centerLogo = centerLogoPath ? `${domain}/ExternalImages/${cleanPath(centerLogoPath)}` : null;

    let logoToUse = logo; // Default imported logo
    let sidebarLogoUrl = null;

    if (roleId == "2") sidebarLogoUrl = orgLogo;
    else if (roleId == "3") sidebarLogoUrl = centerLogo;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // try {
    //     doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);
    // } catch (err) {
    //     console.error("Error adding image to PDF:", err);
    //     if (logoToUse !== logo) {
    //          doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
    //     }
    // }

    const title = "ALL Subjects";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter((column) => column.accessorKey !== "actions");

    const headers = includedColumns.map((col) => col.header);

    const bodyData = subjects.map((row, index) => {
      return includedColumns.map((column) => {
          if (column.accessorKey === 'mrt-row-numbers') return index + 1;
          return row[column.accessorKey] || "";
      });
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("SubjectsList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const handleEditClick = async (rowId) => {
    const response = await get(`Subject/GetSubjectById?subjectId=${rowId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const subject = response.subject;

    reset({
      id: subject.id,
      name: subject.name,
      organizationId: subject.organizationId,
      code: subject.code,
      createdBy: subject.createdBy,
      updatedBy: localStorage.getItem("userId"),
    });
  };
  const handleDeleteClick = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Subject?"
    );

    if (confirmDelete) {
      try {
        const response = await get(`Subject/DeleteSubjectById?id=${rowId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        fetchsubjects();
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post("Subject/AddUpdateSubjectAsync", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          organizationId: organizationId,
          code: "",
          createdBy: localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
        });
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Subjects</h2>
      </div>
      <form
        className="row g-3 new-form-style"
        onSubmit={handleSubmit(saveData)}
      >
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Subject Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`name`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Subject Code<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register(`code`, {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("code")}
                  />
                  {errors.code && (
                    <small className="text-danger">{errors.code.message}</small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Save" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div
                className="d-flex justify-content-end"
                style={{ marginTop: 0 }}
              >
              </div>
              <div className="table-container fixed-last-column-table">
                <CustomDatatableGrid
                  columns={columns}
                  data={subjects}
                  onExportExcel={exportToExcel}
                  onExportPDF={exportToPDF}
                  initialState={{
                    columnPinning: { right: ["actions"] },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default SubjectForm;
